/**
 * Put plain text on the clipboard (CD-017). The Clipboard API first; where it
 * is refused - inside a frame that was not granted `clipboard-write`, which is
 * how Loupe shows this application - the older selection copy.
 *
 * `host` is where the temporary textarea goes. Inside a modal dialog it has to
 * be the dialog itself: a textarea outside it would be focused out of the
 * dialog's focus trap, which takes the focus - and the selection - back
 * before the copy runs.
 */
export interface ClipboardDeps {
  clipboard?: Pick<Clipboard, "writeText">;
  document: Pick<Document, "createElement" | "execCommand">;
}

export async function copyText(
  text: string,
  host: HTMLElement,
  deps: ClipboardDeps = { clipboard: navigator.clipboard, document },
): Promise<boolean> {
  try {
    if (deps.clipboard) {
      await deps.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Refused or unavailable - try the selection copy below.
  }
  const area = deps.document.createElement("textarea");
  area.value = text;
  area.setAttribute("readonly", "");
  area.style.position = "fixed";
  area.style.opacity = "0";
  host.appendChild(area);
  try {
    area.select();
    return deps.document.execCommand("copy");
  } catch {
    return false;
  } finally {
    area.remove();
  }
}
